const APP_PREFIX = import.meta.env.VITE_TOKEN_STORAGE_PREFIX || 'tog';

const ACCESS_TOKEN_KEY = `${APP_PREFIX}_access_token`;
const REFRESH_TOKEN_KEY = `${APP_PREFIX}_refresh_token`;
const TOKEN_EXPIRY_KEY = `${APP_PREFIX}_token_expiry`;

export type AccessToken = string | null;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export function setAuthTokens(tokens: AuthTokens): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);

  const expiryTime = Date.now() + tokens.expiresIn * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
}

export function getAccessToken(): AccessToken {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getAuthTokens(): AuthTokens | null {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!accessToken || !refreshToken || !expiryStr) {
    return null;
  }

  const expiresIn = Math.floor((parseInt(expiryStr) - Date.now()) / 1000);

  return {
    accessToken,
    refreshToken,
    expiresIn,
  };
}

export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

export function onAccessTokenChange(
  listener: (token: AccessToken) => void,
): () => void {
  function handleStorage(event: StorageEvent) {
    // `key` is null when another tab clears all of localStorage
    if (event.key === ACCESS_TOKEN_KEY || event.key === null) {
      listener(getAccessToken());
    }
  }

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}

export function isTokenExpired(): boolean {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return true;

  const expiry = parseInt(expiryStr, 10);
  return Date.now() >= expiry - 60000; // 1 minute buffer
}

export function hasValidToken(): boolean {
  return !!getAccessToken() && !isTokenExpired();
}
